/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @nx/enforce-module-boundaries */
import {
  Component,
  effect,
  inject,
  OnDestroy,
  OnInit,
  Renderer2,
  signal,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NavigationEnd, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { filter, Subscription } from 'rxjs';
import { KanziPrimeUiModule, MenuItem } from '@kanzi-apes/kanzi-prime-ui';
import {
  KanziUiTopBarComponent,
  MainMenuService,
  KanziUiMainSidebarComponent,
  LayoutService,
  KanziUiBreadcrumbComponent,
} from '@kanzi-apes/kanzi-ui';
import { KanziUiModule } from '@kanzi-apes/kanzi-ui';
import { ItemsMenu } from '../configs/main-menu';
import kanziVersion from 'kanziVersion.json';
import * as Locations from '@kanzi-apes/kanzi-location';
import { AlertVoiceService, KanziAlertsModule } from '@kanzi-apes/kanzi-alerts';
import { KanziCustomerModule } from '@kanzi-apes/kanzi-customer';
import { _, TranslateModule, TranslateService } from '@ngx-translate/core';
import * as layoutStore from '../+state/layout/layout.reducer';
import * as layoutSelectors from '../+state/layout/layout.selectors';
import * as layoutActions from '../+state/layout/layout.actions';
import * as MonitoringStore from '../+state/monitoring/monitoring.reducer';
import * as MonitoringSelectors from '../+state/monitoring/monitoring.selectors';
import * as MonitoringActions from '../+state/monitoring/monitoring.actions';
import * as AuthStore from '@kanzi-apes/kanzi-auth';
import * as fromCustomer from '@kanzi-apes/kanzi-customer';
import * as CustomerActions from '@kanzi-apes/kanzi-customer';
import * as lcStorage from '@kanzi-apes/kanzi-utils';
import { menuValidator } from '@kanzi-apes/kanzi-ui-configs';
import { LoginService } from '@kanzi-apes/kanzi-auth';
import { CustomerModel, LocationModel } from '@kanzi-apes/kanzi-models';
import { environment } from '../../environments/environment';
import { JoyrideService } from 'ngx-joyride';
import { KanziAnalyticsReportsService } from '@kanzi-apes/kanzi-analytics-reports';

@Component({
    selector: 'kanzi-apes-layout',
    imports: [
        CommonModule,
        KanziPrimeUiModule,
        KanziUiModule,
        KanziAlertsModule,
        KanziCustomerModule,
        KanziUiTopBarComponent,
        KanziUiMainSidebarComponent,
        TranslateModule,
        KanziUiBreadcrumbComponent,
    ],
    providers: [],
    templateUrl: './layout.component.html',
    styleUrl: './layout.component.scss'
})
export class LayoutComponent implements OnInit, OnDestroy {
  layoutService: LayoutService;
  analyticsReportsService = inject(KanziAnalyticsReportsService);
  translateService: TranslateService = inject(TranslateService);
  mainMenuService: MainMenuService = inject(MainMenuService);
  loginService: LoginService = inject(LoginService);
  router: Router = inject(Router);
  joyrideService: JoyrideService = inject(JoyrideService);
  storeAuth: Store<AuthStore.AuthState> = inject(Store);
  storeLocations: Store<Locations.LocationsState> = inject(Store);
  storeLayout: Store<layoutStore.LayoutState> = inject(Store);
  storeMonitoring: Store<MonitoringStore.MonitoringState> = inject(Store);
  storeCustomer: Store<fromCustomer.State> = inject(Store);
  alertVoiceService = inject(AlertVoiceService);
  progressBar = signal(true);
  itemsMenu = signal<MenuItem[]>([]);
  kanziAppVersion = signal('');
  languageSelected = this.storeLayout.selectSignal(
    layoutSelectors.getLayoutLanguage
  );
  monitoringStatus = this.storeMonitoring.selectSignal(
    MonitoringSelectors.getMonitoringStatus
  );
  monitoringStatusMsg = this.storeMonitoring.selectSignal(
    MonitoringSelectors.getMonitoringStatusMsg
  );
  customers = this.storeCustomer.selectSignal(fromCustomer.getCustomersList);
  customerSelected = this.storeCustomer.selectSignal(
    fromCustomer.getCustomerSelected
  );
  warehouseRootList = this.storeLocations.selectSignal(
    Locations.getLocationRootList
  );
  userWarehouseRootSelected = this.storeLocations.selectSignal(
    Locations.getLocationRootSelected
  );
  user = this.storeAuth.selectSignal(AuthStore.getAuthUser);
  isLogged = this.storeAuth.selectSignal(AuthStore.getAuthLoggedIn);
  token = this.storeAuth.selectSignal(AuthStore.getAuthToken);
  overlayMenuOpenSubscription: Subscription;
  topbarMenuOpenSubscription: Subscription;
  menuProfileOpenSubscription: Subscription;
  menuOutsideClickListener: any;
  menuScrollListener: any;
  topbarMenuOutsideClickListener: any;
  menuProfileOutsideClickListener: any;
  helpdeskUrl = environment.helpdeskUrl || '';

  @ViewChild(KanziUiTopBarComponent) appTopbar!: KanziUiTopBarComponent;
  @ViewChild(KanziUiMainSidebarComponent)
  appSidebar!: KanziUiMainSidebarComponent;

  langEffects = effect(() => {
    this.translateService.use(this.languageSelected());
  });

  monitoringEffects = effect(() => {
    if (
      this.monitoringStatus() &&
      this.monitoringStatus().actual !== this.monitoringStatus().previous
    ) {
      const msg = this.monitoringStatusMsg();
      if (msg) {
        this.alertVoiceService.message(msg);
      }
    }
  });

  isLoggedEffects = effect(
    () => {
      if (this.isLogged()) {
        this.storeAuth.dispatch(AuthStore.userProfileLoad());
      }
    },
    { allowSignalWrites: true }
  );

  userProfileEffects = effect(
    () => {
      if (this.user()) {
        const user = this.user();
        let localState = lcStorage.loadState();
        localState = {
          ...localState,
          auth: {
            loggedIn: this.isLogged(),
            token: this.token(),
            user: this.user(),
          },
        };
        lcStorage.saveState(localState);
        const kanziTourData = lcStorage.loadKanziTour('KanziMaintenanceTour');
        if (user && user.configs) {
          if (user.configs.audit_locations) {
            this.storeLocations.dispatch(
              Locations.loadLocationRootListSuccess({
                listItems: user.configs.audit_locations,
              })
            );
          }
          if (
            user.configs.default_location &&
            !this.userWarehouseRootSelected()
          ) {
            this.storeLocations.dispatch(
              Locations.selectLocationRoot({
                location: user.configs.default_location,
              })
            );
          }
          if (environment.tenantId) {
            this.analyticsReportsService
              .getReportList(environment.tenantId)
              .subscribe((dashboardList) => {
                if (dashboardList && dashboardList.count > 0) {
                  // Find the index of the dashboards menu
                  const dashboardIndex = ItemsMenu.findIndex(
                    (items) => items.label === 'menu_main.dashboards'
                  );
                  // Add the dashboards to the menu
                  ItemsMenu[dashboardIndex].items =
                    dashboardList.dashboards.map((item) => {
                      let menuItem: MenuItem = {
                        label: item.dashboardMenuLabel || item.dashboardName,
                        icon:
                          item.dashboardMenuIcon || 'pi pi-fw pi-chart-line',
                        routerLink: item.routerLink || 'analytics/embed-report',
                      };
                      if (!item.withoutQueryParams) {
                        menuItem = {
                          ...menuItem,
                          queryParams: {
                            reportName: item.dashboardName,
                          },
                        };
                      }
                      console.log('Menu Item', menuItem);
                      return menuItem;
                    });
                }
                if (user.configs) {
                  this.itemsMenu.set(
                    menuValidator(ItemsMenu, user.configs.active_web_modules)
                  );
                }
              });
          } else {
            this.itemsMenu.set(
              menuValidator(ItemsMenu, user.configs.active_web_modules)
            );
          }

          if (this.loginService.isParent(user)) {
            this.storeLayout.dispatch(layoutActions.activateCustomerParent());
            this.storeCustomer.dispatch(
              new fromCustomer.LoadList({
                page: 1,
                page_size: 10,
                parent_id: user.configs.customer.id,
              })
            );
          } else {
            this.storeCustomer.dispatch(
              new fromCustomer.Selected(user.configs.customer)
            );
          }
        }
      }
    },
    { allowSignalWrites: true }
  );

  customerSelectedEffects = effect(
    () => {
      let localState = lcStorage.loadState();
      if (!this.customerSelected()) {
        if (localState && localState.customer) {
          this.storeCustomer.dispatch(
            new CustomerActions.Selected(localState.customer)
          );
        }
      } else {
        localState = {
          ...localState,
          customer: this.customerSelected(),
        };
        lcStorage.saveState(localState);
      }
    },
    { allowSignalWrites: true }
  );

  constructor(public renderer: Renderer2) {
    this.layoutService = inject(LayoutService);
    this.kanziAppVersion.set(kanziVersion.version);
    this.hideMenuProfile();

    this.overlayMenuOpenSubscription =
      this.layoutService.overlayOpen$.subscribe(() => {
        this.hideTopbarMenu();

        if (!this.menuOutsideClickListener) {
          this.menuOutsideClickListener = this.renderer.listen(
            'document',
            'click',
            (event) => {
              const isOutsideClicked = !(
                this.appSidebar.el.nativeElement.isSameNode(event.target) ||
                this.appSidebar.el.nativeElement.contains(event.target) ||
                this.appTopbar.menuButton.nativeElement.isSameNode(
                  event.target
                ) ||
                this.appTopbar.menuButton.nativeElement.contains(event.target)
              );
              if (isOutsideClicked) {
                this.hideMenu();
              }
            }
          );
        }

        if (
          (this.layoutService.isHorizontal() ||
            this.layoutService.isSlim() ||
            this.layoutService.isSlimPlus()) &&
          !this.menuScrollListener
        ) {
          this.menuScrollListener = this.renderer.listen(
            this.appSidebar.menuContainer.nativeElement,
            'scroll',
            () => {
              if (this.layoutService.isDesktop()) {
                this.hideMenu();
              }
            }
          );
        }

        if (this.layoutService.state.staticMenuMobileActive) {
          this.blockBodyScroll();
        }
      });

    this.topbarMenuOpenSubscription =
      this.layoutService.topbarMenuOpen$.subscribe(() => {
        if (!this.topbarMenuOutsideClickListener) {
          this.topbarMenuOutsideClickListener = this.renderer.listen(
            'document',
            'click',
            (event) => {
              const isOutsideClicked = !(
                this.appTopbar.el.nativeElement.isSameNode(event.target) ||
                this.appTopbar.el.nativeElement.contains(event.target) ||
                this.appTopbar.mobileMenuButton.nativeElement.isSameNode(
                  event.target
                ) ||
                this.appTopbar.mobileMenuButton.nativeElement.contains(
                  event.target
                )
              );
              if (isOutsideClicked) {
                this.hideTopbarMenu();
              }
            }
          );
        }

        if (this.layoutService.state.staticMenuMobileActive) {
          this.blockBodyScroll();
        }
      });

    this.menuProfileOpenSubscription =
      this.layoutService.menuProfileOpen$.subscribe(() => {
        this.hideMenu();

        if (!this.menuProfileOutsideClickListener) {
          this.menuProfileOutsideClickListener = this.renderer.listen(
            'document',
            'click',
            (event) => {
              const isOutsideClicked = !(
                this.appSidebar.menuProfile.el.nativeElement.isSameNode(
                  event.target
                ) ||
                this.appSidebar.menuProfile.el.nativeElement.contains(
                  event.target
                )
              );
              if (isOutsideClicked) {
                this.hideMenuProfile();
              }
            }
          );
        }
      });

    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe(() => {
        this.hideMenu();
        this.hideTopbarMenu();
        this.hideMenuProfile();
      });
  }

  blockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.add('blocked-scroll');
    } else {
      document.body.className += ' blocked-scroll';
    }
  }

  unblockBodyScroll(): void {
    if (document.body.classList) {
      document.body.classList.remove('blocked-scroll');
    } else {
      document.body.className = document.body.className.replace(
        new RegExp(
          '(^|\\b)' + 'blocked-scroll'.split(' ').join('|') + '(\\b|$)',
          'gi'
        ),
        ' '
      );
    }
  }

  hideMenu() {
    this.layoutService.state.overlayMenuActive = false;
    this.layoutService.state.staticMenuMobileActive = false;
    this.layoutService.state.menuHoverActive = false;
    this.mainMenuService.reset();

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
      this.menuOutsideClickListener = null;
    }

    if (this.menuScrollListener) {
      this.menuScrollListener();
      this.menuScrollListener = null;
    }
    this.unblockBodyScroll();
  }

  hideTopbarMenu() {
    this.layoutService.state.topbarMenuActive = false;

    if (this.topbarMenuOutsideClickListener) {
      this.topbarMenuOutsideClickListener();
      this.topbarMenuOutsideClickListener = null;
    }
  }

  hideMenuProfile() {
    this.layoutService.state.menuProfileActive = false;

    if (this.menuProfileOutsideClickListener) {
      this.menuProfileOutsideClickListener();
      this.menuProfileOutsideClickListener = null;
    }
  }

  get containerClass() {
    const styleClass: { [key: string]: any } = {
      'layout-overlay': this.layoutService.config().menuMode === 'overlay',
      'layout-static': this.layoutService.config().menuMode === 'static',
      'layout-slim': this.layoutService.config().menuMode === 'slim',
      'layout-slim-plus': this.layoutService.config().menuMode === 'slim-plus',
      'layout-horizontal':
        this.layoutService.config().menuMode === 'horizontal',
      'layout-reveal': this.layoutService.config().menuMode === 'reveal',
      'layout-drawer': this.layoutService.config().menuMode === 'drawer',
      'p-input-filled': this.layoutService.config().inputStyle === 'filled',
      'p-ripple-disabled': !this.layoutService.config().ripple,
      'layout-static-inactive':
        this.layoutService.state.staticMenuDesktopInactive &&
        this.layoutService.config().menuMode === 'static',
      'layout-overlay-active': this.layoutService.state.overlayMenuActive,
      'layout-mobile-active': this.layoutService.state.staticMenuMobileActive,
      'layout-topbar-menu-active': this.layoutService.state.topbarMenuActive,
      'layout-menu-profile-active': this.layoutService.state.menuProfileActive,
      'layout-sidebar-active': this.layoutService.state.sidebarActive,
      'layout-sidebar-anchored': this.layoutService.state.anchored,
    };
    styleClass['layout-topbar-' + this.layoutService.config().topbarTheme] =
      true;
    styleClass['layout-menu-' + this.layoutService.config().menuTheme] = true;
    styleClass[
      'layout-menu-profile-' + this.layoutService.config().menuProfilePosition
    ] = true;
    return styleClass;
  }

  logoutSession() {
    localStorage.removeItem('kanziState');
    this.storeAuth.dispatch(AuthStore.logout());
    this.loginService.logout();
  }

  /**
   * Change languaje
   * @param lang {string} - Language
   */
  changeLanguage(lang: string) {
    this.storeLayout.dispatch(layoutActions.changeLanguage({ language: lang }));
  }

  /**
   *
   * @param event {any}
   *
   * Function to know if the browser is online.
   */
  updateNetworkIndicator(event: any) {
    if (event.type === 'online') {
      // this.functions?.changeFavicon('active');
      this.translateService
        .get(_(['app.online', 'app.online_msg']))
        .subscribe((words) => {
          this.storeMonitoring.dispatch(
            MonitoringActions.statusOnline({
              payload: {
                type: 'success',
                title: words['app.online'],
                msg: words['app.online_msg'],
              },
            })
          );
        });
    } else if (event.type === 'offline') {
      // this.functions?.changeFavicon('error');
      this.translateService
        .get(_(['app.offline', 'app.offline_msg']))
        .subscribe((words) => {
          this.storeMonitoring.dispatch(
            MonitoringActions.statusOffline({
              payload: {
                type: 'error',
                title: words['app.offline'],
                msg: words['app.offline_msg'],
              },
            })
          );
        });
    }
  }

  /**
   * Select customer
   * @param event {CustomerModel} - Customer
   */
  selectCustomer(event: CustomerModel) {
    let localState = lcStorage.loadState();
    localState = {
      ...localState,
      customer: event,
    };
    lcStorage.saveState(localState);
    this.storeLayout.dispatch(new CustomerActions.Selected(event));
  }

  /**
   *
   * @param warehouseRoot {LocationModel} - Warehouse Root
   * Select warehouse root
   */
  onSelectWarehouse(warehouseRoot: LocationModel) {
    this.storeLocations.dispatch(
      Locations.selectLocationRoot({ location: warehouseRoot })
    );
  }

  ngOnInit() {
    this.translateService.use(this.languageSelected());
    this.progressBar.set(false);

    window.addEventListener('online', this.updateNetworkIndicator.bind(this));
    window.addEventListener('offline', this.updateNetworkIndicator.bind(this));
  }

  ngOnDestroy() {
    if (this.overlayMenuOpenSubscription) {
      this.overlayMenuOpenSubscription.unsubscribe();
    }

    if (this.menuOutsideClickListener) {
      this.menuOutsideClickListener();
    }
  }
}
